<template>
   <Modal
      v-if="isOpen"
      id="study-withdraw-step-2"
      ref="modalRef"
      :key="studyId"
      width="sm"
      :pill="{ text: $t('account.withdrawal.modal_label'), theme: Theme.Danger }"
      @close="emit('end-withdrawal')"
   >
      <template #title>
         <h1 class="text-regular grow text-center text-xl text-black md:text-2xl">
            {{ $t("account.withdrawal.step2.heading") }}
         </h1>
      </template>
      <form>
         <fieldset class="mb-6" :aria-describedby="errorList.length ? 'withdrawal-reasons-error' : null">
            <legend class="mb-4 text-bolder">{{ $t("account.withdrawal.step2.reasons_label") }}</legend>
            <div class="rounded-md border border-grey-10 p-4">
               <CheckboxInput
                  v-for="reason in withdrawalReasonsResp"
                  :id="reason.id"
                  :key="reason.id"
                  :value="withdrawalReasonsInput.includes(reason.id)"
                  :label="reason.label"
                  :group="`withdrawal-reason-${reason.id}`"
                  @update:value="updateWithdrawalReasonsInput(reason.id, $event)"
               />
            </div>
            <InvalidInputsList
               id="withdrawal-reasons-error"
               :errorList="errorList"
               :class="{ hidden: !errorList.length }"
            />
         </fieldset>

         <label for="withdrawal-comments" class="mb-4 block text-bolder"
            >{{ $t("account.withdrawal.step2.comments_label") }} ({{ $t("validation.optional_input") }})</label
         >
         <textarea
            id="withdrawal-comments"
            v-model="additionalCommentsInput"
            data-cy="withdrawal-comments-input"
            class="w-full rounded-md border border-grey-10 p-2"
         ></textarea>
      </form>
      <template #action-btns>
         <DefaultButton
            data-cy="test-modal-cancel-btn"
            :color="Theme.Grey"
            size="sm"
            class="w-full"
            @click="emit('go-back')"
            >{{ $t("account.withdrawal.step2.button_back") }}</DefaultButton
         >
         <LoadingButton
            :isLoading="isSubmitting"
            data-cy="test-modal-confirm-btn"
            :color="Theme.Danger"
            size="sm"
            class="w-full"
            @click="onWithdrawSubmit"
            >{{ $t("account.withdrawal.button_confirm") }}</LoadingButton
         >
      </template>
   </Modal>
</template>
<script setup lang="ts">
import { Theme } from "@/types";
import { useVuelidate } from "@vuelidate/core";
import type { Validation } from "@vuelidate/core";
import StudiesAPI, { type StudyWithdrawalReason } from "@/data/api-layer/studiesApi";
import { required, helpers } from "@vuelidate/validators";
import Modal from "@/components/ui/Modal.vue";
import { useToastKey } from "@/plugins/useToast";

interface Props {
   withdrawalReasonsResp: StudyWithdrawalReason[];
   studyId: string;
   isOpen: boolean;
}

// declaration
// -----------------------------------------
const $t = useI18n().t;
const useToast = inject(useToastKey);
const emit = defineEmits(["end-withdrawal", "go-back", "confirm"]);

const props = defineProps<Props>();

// state
const modalRef: Ref<InstanceType<typeof Modal>> = ref(null);
const isSubmitting = ref(false);
const withdrawalReasonsInput = ref<string[]>([]);
const additionalCommentsInput = ref<string>("");

// Validation & rules
const rules = {
   withdrawalReasonsInput: {
      required: helpers.withMessage($t("validation.field_required"), required),
      $lazy: true,
   },
};

const v$: Ref<Validation> = useVuelidate(rules, { withdrawalReasonsInput });

// computed
// -----------------------------------------
const errorList = computed(() => {
   if (v$.value.$invalid)
      return [
         {
            inputId: "withdrawal-reasons",
            validator: "required",
            message: $t("validation.choice.required"),
         },
      ];
   return [];
});

// Methods
// -----------------------------------------
async function onWithdrawSubmit() {
   const isFormCorrect = await v$.value.$validate();

   if (!isFormCorrect) return;

   try {
      isSubmitting.value = true;
      await StudiesAPI.withdrawFromStudy(props.studyId, {
         withdrawal_reason_ids: withdrawalReasonsInput.value,
         withdrawal_comments: additionalCommentsInput.value.trim() || null,
      });
      emit("confirm");
   } catch (error) {
      emit("end-withdrawal");
      console.error("Error posting data", error);
      useToast({ text: $t("account.withdrawal.withdrawal_error"), type: "error" });
   } finally {
      isSubmitting.value = false;
   }
}

function updateWithdrawalReasonsInput(id, isChecked) {
   if (isChecked) {
      if (!withdrawalReasonsInput.value.includes(id)) {
         withdrawalReasonsInput.value.push(id);
      }
   } else {
      withdrawalReasonsInput.value = withdrawalReasonsInput.value.filter((inputId) => inputId !== id);
   }
}

// Expose the closeModal method that comes from the Modal component to the parent
function closeModal() {
   modalRef.value.closeModal();
}

// Define expose
defineExpose({
   closeModal,
});
</script>
